import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { getUploadUrl } from '../utils/helpers';
import { API_BASE_URL } from '../services/api';
import * as authService from '../services/authService';

const Header = (props) => {
  const { user, role, logout, setUser } = useAuth();
  if ((role === 'admin' || role === 'employee') && !props.isLayout) return null;

  const [photoError, setPhotoError] = useState(false);

  useEffect(() => {
    setPhotoError(false);
  }, [user?.profile_picture, user?.avatar_url]);

  useEffect(() => {
    let active = true;
    const fetchLatestUser = async () => {
      try {
        const latestUser = await authService.getCurrentUser();
        if (active && latestUser && setUser) {
          setUser(latestUser);
        }
      } catch (err) {
        console.error('Header user profile refresh error:', err);
      }
    };
    if (user) {
      fetchLatestUser();
    }
    return () => {
      active = false;
    };
  }, []);

  // Dynamic screen width detection for mobile/tablet navbar rules
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileOrTablet(window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getLogoUrl = () => {
    const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
    return `${apiBase}/logo.php`;
  };

  const getTodayDateStr = () => {
    const d = new Date();
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  let headerTitle = 'PranaFlow Portal';
  let panelName = 'LMS Panel';
  let avatar = null;

  if (role === 'admin') {
    headerTitle = 'PranaFlow Admin';
    panelName = 'Admin Portal';
  } else if (role === 'employee') {
    headerTitle = 'PranaFlow Employees';
    panelName = user?.name || 'Faculty Panel';
  } else if (role === 'student' || role === 'demo_student') {
    headerTitle = role === 'demo_student' ? 'PranaFlow Demo Students' : 'PranaFlow Students';
    panelName = user?.name || (role === 'demo_student' ? 'Demo Student' : 'Student Account');
  }

  const logoUrl = getLogoUrl();
  const rawPhoto = (role === 'student' || role === 'demo_student') ? null : (user?.avatar_url || user?.profile_picture);
  const isFallback = !rawPhoto || photoError;
  const avatarSrc = isFallback ? logoUrl : getUploadUrl(rawPhoto, logoUrl);

  avatar = (
    <img
      src={avatarSrc}
      alt="Avatar"
      className={`student-avatar ${isFallback ? 'fallback-logo' : ''}`}
      onError={() => setPhotoError(true)}
      style={isFallback 
        ? { objectFit: 'contain', backgroundColor: '#ffffff', padding: '4px', imageOrientation: 'from-image' } 
        : { objectFit: 'cover', imageOrientation: 'from-image' }
      }
    />
  );

  const handleToggleDrawer = () => {
    window.dispatchEvent(new CustomEvent('toggle-student-drawer'));
  };

  const isStudent = role === 'student' || role === 'demo_student';

  // Inline styles to guarantee centering on mobile/tablet screens (Vite / cache proof)
  const headerMobileStyle = isMobileOrTablet ? {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    zIndex: 1000,
    backgroundColor: '#ffffff',
    height: window.innerWidth <= 480 ? '60px' : '70px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 16px',
    borderBottom: '1px solid var(--border-light)'
  } : {};

  const hamburgerMobileStyle = isMobileOrTablet ? {
    position: 'absolute',
    left: window.innerWidth <= 480 ? '12px' : '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    zIndex: 20
  } : {};

  const logoMobileStyle = isMobileOrTablet ? {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: window.innerWidth <= 360 ? '4px' : '8px',
    margin: 0,
    textDecoration: 'none',
    pointerEvents: 'auto',
    whiteSpace: 'nowrap',
    zIndex: 10
  } : {
    marginBottom: 0,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  };

  return (
    <header 
      className={isStudent ? 'lms-header premium-student-header student-portal-header' : 'lms-header'}
      style={headerMobileStyle}
    >
      <div 
        className="lms-header-left" 
        style={(isStudent && isMobileOrTablet) ? { width: '100%', height: '100%', position: 'relative' } : { display: 'flex', alignItems: 'center', gap: '8px' }}
      >
        {(role === 'student' || role === 'demo_student' || role === 'employee' || role === 'admin') && (
          <button
            onClick={handleToggleDrawer}
            className="student-mobile-menu-btn"
            aria-label="Toggle menu"
            style={hamburgerMobileStyle}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        )}
        <Link 
          to={role === 'demo_student' ? '/demo/dashboard' : `/${role}/dashboard`} 
          className="login-logo" 
          style={logoMobileStyle}
        >
          <img src={getLogoUrl()} className="brand-logo student-brand-logo" alt="PranaFlow Academy" />
          <span className="student-navbar-title">{headerTitle}</span>
        </Link>
      </div>

      <div className={(role === 'student' || role === 'demo_student' || role === 'employee' || role === 'admin') ? "lms-header-right student-header-right-desktop psh-right" : "lms-header-right"}>
        {isStudent && (
          <div className="psh-date-chip">
            <svg viewBox="0 0 24 24" style={{ width: '14px', height: '14px', fill: 'currentColor' }}>
              <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2zm-7 5h5v5h-5z"/>
            </svg>
            <span>Today: {getTodayDateStr()}</span>
          </div>
        )}
        <div className="student-profile-info" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {avatar}
          <div className="student-name">
            <span className="student-name-desktop">{panelName}</span>
          </div>
          <button className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '0.8rem', height: '34px', borderRadius: '8px' }} onClick={logout}>
            Logout
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
